<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useModuleGate } from 'dashboard/composables/useModuleGate';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import claudeConnectionsAPI from 'dashboard/api/claudeConnections';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
useModuleGate('claude');

const isLoading = ref(true);
const activeAction = ref('');
const mcpUrl = ref('');
const connections = ref([]);
const approvals = ref([]);
let refreshTimer;

const steps = computed(() => [
  t('INTEGRATION_SETTINGS.CLAUDE.STEP_1'),
  t('INTEGRATION_SETTINGS.CLAUDE.STEP_2'),
  t('INTEGRATION_SETTINGS.CLAUDE.STEP_3'),
]);

const formatDate = value =>
  value
    ? new Date(value).toLocaleString()
    : t('INTEGRATION_SETTINGS.CLAUDE.NEVER');

const conversationUrl = id =>
  frontendURL(`accounts/${route.params.accountId}/conversations/${id}`);

const load = async () => {
  try {
    const { data } = await claudeConnectionsAPI.get();
    mcpUrl.value = data.mcp_url;
    connections.value = data.connections;
    approvals.value = data.approvals;
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.CLAUDE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const copyUrl = async () => {
  await navigator.clipboard.writeText(mcpUrl.value);
  useAlert(t('INTEGRATION_SETTINGS.CLAUDE.COPIED'));
};

const run = async (key, action, successKey) => {
  activeAction.value = key;
  try {
    await action();
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    useAlert(t(successKey));
    await load();
  } catch (error) {
    useAlert(
      error.response?.data?.error_message ||
        t('INTEGRATION_SETTINGS.CLAUDE.ACTION_ERROR')
    );
    await load();
  } finally {
    activeAction.value = '';
  }
};

const approve = approval =>
  run(
    `approve-${approval.id}`,
    () => claudeConnectionsAPI.approve(approval.id),
    'INTEGRATION_SETTINGS.CLAUDE.APPROVED'
  );

const reject = approval =>
  run(
    `reject-${approval.id}`,
    () => claudeConnectionsAPI.reject(approval.id),
    'INTEGRATION_SETTINGS.CLAUDE.REJECTED'
  );

const disconnect = connection =>
  run(
    `disconnect-${connection.id}`,
    () => claudeConnectionsAPI.delete(connection.id),
    'INTEGRATION_SETTINGS.CLAUDE.DISCONNECTED'
  );

onMounted(() => {
  load();
  refreshTimer = window.setInterval(load, 15000);
});

onBeforeUnmount(() => window.clearInterval(refreshTimer));
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.CLAUDE.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.CLAUDE.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.CLAUDE.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <section
          v-if="approvals.length"
          class="flex flex-col gap-3"
          data-test="claude-approvals"
        >
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.CLAUDE.APPROVALS') }}
          </h3>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.CLAUDE.APPROVALS_HINT') }}
          </p>
          <article
            v-for="approval in approvals"
            :key="approval.id"
            class="flex flex-col gap-3 rounded-xl border border-n-amber-7 bg-n-amber-3 p-4"
          >
            <div class="flex items-start justify-between gap-3">
              <p class="m-0 text-sm text-n-slate-11">
                {{
                  $t('INTEGRATION_SETTINGS.CLAUDE.REQUESTED_BY', {
                    name: approval.requested_by,
                  })
                }}
              </p>
              <router-link
                v-if="approval.conversation_id"
                :to="conversationUrl(approval.conversation_id)"
                class="text-sm text-n-blue-11"
              >
                {{
                  $t('INTEGRATION_SETTINGS.CLAUDE.OPEN_CONVERSATION', {
                    id: approval.conversation_id,
                  })
                }}
              </router-link>
            </div>
            <p
              class="m-0 whitespace-pre-wrap rounded-lg bg-n-solid-1 p-3 text-sm text-n-slate-12"
            >
              {{ approval.content }}
            </p>
            <div class="flex gap-2">
              <Button
                :label="$t('INTEGRATION_SETTINGS.CLAUDE.APPROVE')"
                :is-loading="activeAction === `approve-${approval.id}`"
                @click="approve(approval)"
              />
              <Button
                :label="$t('INTEGRATION_SETTINGS.CLAUDE.REJECT')"
                variant="faded"
                color="ruby"
                :is-loading="activeAction === `reject-${approval.id}`"
                @click="reject(approval)"
              />
            </div>
          </article>
        </section>

        <section
          class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-5"
        >
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.CLAUDE.HOW_TITLE') }}
          </h3>
          <ol class="m-0 flex flex-col gap-2 pl-5 text-sm text-n-slate-12">
            <li v-for="step in steps" :key="step">{{ step }}</li>
          </ol>
          <div class="flex flex-wrap items-center gap-2">
            <code
              class="rounded-lg bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              data-test="claude-mcp-url"
            >
              {{ mcpUrl }}
            </code>
            <Button
              :label="$t('INTEGRATION_SETTINGS.CLAUDE.COPY_URL')"
              variant="faded"
              size="sm"
              icon="i-lucide-copy"
              @click="copyUrl"
            />
          </div>
          <p class="m-0 text-xs text-n-slate-10">
            {{ $t('INTEGRATION_SETTINGS.CLAUDE.SAFETY_NOTE') }}
          </p>
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.CLAUDE.CONNECTIONS') }}
          </h3>
          <p v-if="!connections.length" class="m-0 text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.CLAUDE.EMPTY') }}
          </p>
          <article
            v-for="connection in connections"
            :key="connection.id"
            class="flex items-center justify-between gap-3 rounded-xl border border-n-weak p-4"
          >
            <div>
              <p class="m-0 font-medium text-n-slate-12">
                {{
                  $t('INTEGRATION_SETTINGS.CLAUDE.CONNECTION_LABEL', {
                    app: connection.app_name,
                    user: connection.user_name,
                  })
                }}
              </p>
              <p class="mb-0 mt-1 text-xs text-n-slate-10">
                {{
                  $t('INTEGRATION_SETTINGS.CLAUDE.LAST_USED', {
                    date: formatDate(connection.last_used_at),
                  })
                }}
              </p>
            </div>
            <Button
              :label="$t('INTEGRATION_SETTINGS.CLAUDE.DISCONNECT')"
              variant="faded"
              color="ruby"
              size="sm"
              :is-loading="activeAction === `disconnect-${connection.id}`"
              @click="disconnect(connection)"
            />
          </article>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
